import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import i18n from "@/i18n";

import cls from "./Wallet.module.scss";

import coinIcon from "@/assets/UI/coin.webp";
import mrImg from "@/assets/UI/memberCreate/q20Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q20MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q20Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q20MrsM.webp";

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const formatDate = (value) => {
  const d = safeDate(value);
  if (!d) return "—";
  return d.toLocaleDateString();
};

const formatNoty = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatCad = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
  });

export default function Wallet() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (key, fallback) => i18n.t(key, { defaultValue: fallback });

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/v1/member/wallet/overview");
        if (!isMounted) return;
        setOverview(data);
        setError("");
      } catch (err) {
        console.error("wallet overview error", err);
        if (!isMounted) return;
        setError(
          t(
            "wallet.loadError",
            "We couldn’t load your wallet details. Please try again.",
          ),
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const wallet = overview?.wallet;
  const exchangeRate = overview?.exchangeRate;
  const paymentSubmissions = overview?.recentPaymentSubmissions || [];

  const balanceNoty = wallet?.balanceNoty ?? 0;
  const statusText = wallet?.status || t("wallet.statusActive", "active");

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
        <div className={cls.stageGlow} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.walletCard}>
            {/* Header */}
            <header className={cls.cardHeader}>
              <div className={cls.headerTopRow}>
                <div className={cls.headerText}>
                  <p className={cls.kicker}>
                    {t("wallet.kicker", "Member Wallet")}
                  </p>

                  <h1 className={cls.title}>
                    {t("wallet.title", "Your Noty Wallet")}
                  </h1>

                  <p className={cls.subtitle}>
                    {t(
                      "wallet.subtitle",
                      "Balance, rate, refills, and your latest wallet activity.",
                    )}
                  </p>
                </div>
              </div>

              <div className={cls.headerMeta}>
                {error ? <p className={cls.error}>{error}</p> : null}
                {loading ? (
                  <p className={cls.loading}>
                    {t("wallet.loading", "Loading wallet data…")}
                  </p>
                ) : null}
              </div>
            </header>

            {/* Overview */}
            <section className={cls.overviewBlock}>
              <div className={`${cls.overviewItem} ${cls.blueCard}`}>
                <div className={cls.overviewTop}>
                  <div className={cls.coinWrap} aria-hidden="true">
                    <img className={cls.coin} src={coinIcon} alt="" />
                  </div>

                  <div className={cls.overviewHead}>
                    <span className={cls.overviewLabel}>
                      {t("wallet.currentBalance", "Current balance")}
                    </span>
                    <span className={cls.overviewValue}>
                      {formatNoty(balanceNoty)} {t("wallet.notyUnit", "NOTY")}
                    </span>
                  </div>
                </div>

                <div className={cls.overviewMeta}>
                  <span className={`${cls.metaPill} ${cls.pillBlue}`}>
                    {t("wallet.statusLabel", "Status")}:{" "}
                    <b className={cls.pillStrong}>{statusText}</b>
                  </span>

                  <span className={`${cls.metaPill} ${cls.pillDim}`}>
                    {t("wallet.lastUpdateLabel", "Last update")}:{" "}
                    <b className={cls.pillStrong}>
                      {formatDate(wallet?.updatedAt)}
                    </b>
                  </span>
                </div>
              </div>

              <div className={`${cls.overviewItem} ${cls.pinkCard}`}>
                <div className={cls.overviewTop}>
                  <div
                    className={`${cls.coinWrap} ${cls.coinWrapPink}`}
                    aria-hidden="true"
                  >
                    <img className={cls.coin} src={coinIcon} alt="" />
                  </div>

                  <div className={cls.overviewHead}>
                    <span className={cls.overviewLabel}>
                      {t("wallet.exchangeRate.title", "Exchange rate")}
                    </span>
                    <span className={cls.overviewValue}>
                      {t(
                        "wallet.exchangeRate.pattern",
                        "1 CAD = {{rate}} NOTY",
                      ).replace("{{rate}}", String(exchangeRate?.rate ?? 10))}
                    </span>
                  </div>
                </div>

                <div className={cls.overviewMeta}>
                  <span className={`${cls.metaPill} ${cls.pillPink}`}>
                    {t("wallet.exchangeRate.effective", "Effective")}:{" "}
                    <b className={cls.pillStrong}>
                      {formatDate(exchangeRate?.effectiveFrom)}
                    </b>
                  </span>

                  <span className={`${cls.metaPill} ${cls.pillDim}`}>
                    {t(
                      "wallet.exchangeRate.note",
                      "Current rate used to convert CAD payments into Noty Coin.",
                    )}
                  </span>
                </div>
              </div>
            </section>

            {/* Refill */}
            <section className={cls.refillSection}>
              <div className={cls.sectionHeaderRow}>
                <div className={cls.sectionHeaderText}>
                  <h2 className={cls.sectionTitle}>
                    {t("wallet.refill.title", "Refill your wallet")}
                  </h2>

                  {/* Notes */}
                  <div className={cls.noteStack}>
                    <p className={cls.sectionNoteBox}>
                      <span className={cls.noteIcon} aria-hidden="true">
                        ℹ️
                      </span>
                      {t(
                        "wallet.refill.note",
                        "Choose how you want to top up your Noty Coin balance.",
                      )}
                    </p>

                    <p className={cls.sectionNoteBox}>
                      <span className={cls.noteIcon} aria-hidden="true">
                        🌍
                      </span>
                      {t(
                        "wallet.refill.outsideCanada",
                        "If you are from outside of Canada,",
                      )}{" "}
                      <Link className={cls.chatBtn} to="/member/chat">
                        {t("wallet.refill.chatLink", "Chat with our team")}
                      </Link>{" "}
                    </p>

                    <p className={cls.sectionNoteBox}>
                      <span className={cls.noteIcon} aria-hidden="true">
                        🎁
                      </span>
                      {t(
                        "wallet.refill.patreon",
                        "If you are one of our Patreon members,",
                      )}{" "}
                      <Link className={cls.chatBtn} to="/member/chat">
                        {t("wallet.refill.chatLink", "Chat with our team")}
                      </Link>{" "}
                    </p>
                    <p className={cls.sectionNoteBox}>
                      <span className={cls.noteIcon} aria-hidden="true">
                        💝
                      </span>
                      {t(
                        "wallet.refill.patreonJoin",
                        "If you are not our Patreon member, get the membership now,",
                      )}{" "}
                      <a
                        className={cls.chatBtn}
                        href="https://www.patreon.com/cw/CalgaryMates"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("wallet.refill.patreonLink", "Join Patreon")}
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div className={cls.refillGrid}>
                <div className={`${cls.refillCard} ${cls.refillBlue}`}>
                  <div className={cls.refillHead}>
                    <h3>
                      {t(
                        "wallet.refill.bank.title",
                        "E-transfer / Bank transfer",
                      )}
                    </h3>
                    <span className={`${cls.chip} ${cls.chipHot}`}>
                      {t("wallet.refill.bank.chip", "Fast")}
                    </span>
                  </div>

                  <p>
                    {t(
                      "wallet.refill.bank.note",
                      "Send funds directly to our bank account and upload your proof to get credited.",
                    )}
                  </p>

                  <Link
                    className={cls.primaryButton}
                    to="/member/wallet/e-transfer"
                  >
                    {t("wallet.refill.bank.cta", "E-Transfer / Bank Transfer")}
                  </Link>
                </div>

                <div className={`${cls.refillCard} ${cls.refillPink}`}>
                  <div className={cls.refillHead}>
                    <h3>
                      {t("wallet.refill.promo.title", "Redeem promo code")}
                    </h3>
                    <span className={`${cls.chip} ${cls.chipNew}`}>
                      {t("wallet.refill.promo.chip", "Instant")}
                    </span>
                  </div>

                  <p>
                    {t(
                      "wallet.refill.promo.note",
                      "Got a promo code? Redeem it instantly to top up your wallet.",
                    )}
                  </p>

                  <Link
                    className={cls.primaryButton}
                    to="/member/wallet/redeem"
                  >
                    {t("wallet.refill.promo.cta", "Redeem promo code")}
                  </Link>
                </div>
                <div className={`${cls.refillCard} ${cls.refillMuted}`}>
                  <div className={cls.refillHead}>
                    <h3>{t("wallet.refill.online.title", "Pay online")}</h3>
                    <span className={`${cls.chip} ${cls.chipSoon}`}>
                      {t("wallet.refill.online.disabled", "Coming soon")}
                    </span>
                  </div>

                  <p>
                    {t(
                      "wallet.refill.online.note",
                      "We are pending approval for online card payments. This option will unlock soon.",
                    )}
                  </p>

                  <button className={cls.disabledButton} disabled>
                    {t("wallet.refill.online.disabled", "Coming soon")}
                  </button>
                </div>
              </div>
            </section>

            {/* Recent submissions */}
            <section className={cls.sectionCard}>
              <div className={cls.sectionCardHead}>
                <div>
                  <h2 className={cls.sectionTitle}>
                    {t("wallet.topups.title", "Recent top-up submissions")}
                  </h2>
                  <p className={cls.sectionNote}>
                    {t(
                      "wallet.topups.note",
                      "Proofs you have uploaded for e-transfer payments.",
                    )}
                  </p>
                </div>
              </div>

              <div className={cls.list}>
                {paymentSubmissions.length === 0 && !loading ? (
                  <p className={cls.emptyState}>
                    {t(
                      "wallet.topups.empty",
                      "No payment submissions recorded yet.",
                    )}
                  </p>
                ) : null}

                {paymentSubmissions.map((submission) => (
                  <div key={submission._id} className={cls.listItem}>
                    <div className={cls.listLeft}>
                      <span className={cls.listTitle}>
                        {submission.paymentOption?.title ||
                          submission.paymentMethod ||
                          t("wallet.topups.defaultTitle", "Payment submission")}
                      </span>
                      <span className={cls.listMeta}>
                        {formatDate(submission.transferDate)} •{" "}
                        {submission.status ||
                          t("wallet.topups.statusUnknown", "unknown")}
                      </span>
                    </div>

                    <span className={cls.listValue}>
                      {formatCad(submission.amountCad)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
